import type { UserPreferences, UserPreferencesProvider } from '@atlaskit/editor-common/types';

import { getApolloClient } from '@confluence/graphql';

import { EditorPreferencesMutation } from './EditorPreferencesMutation.graphql';
import { EditorPreferencesQuery } from './EditorPreferencesQuery.graphql';
import type {
	EditorPreferencesMutation as EditorPreferencesMutationResponse,
	EditorPreferencesMutationVariables,
} from './__types__/EditorPreferencesMutation';
import type { EditorPreferencesQuery as EditorPreferencesQueryType } from './__types__/EditorPreferencesQuery';
import { parseConfluenceEditorSettings } from './EditorPreferences';

enum Status {
	Complete = 'complete',
	Error = 'error',
	Pending = 'pending',
}

export class EditorUserPreferencesProvider implements UserPreferencesProvider {
	private userPreferences: UserPreferences = {};
	// We use getApolloClient to get the apollo client instance
	// This is to avoid adding required apollo context
	private apolloClient = getApolloClient();
	private providerStatus: Status = Status.Pending;

	public constructor() {
		this.loadPreferencesInternal()
			.then(() => {
				this.providerStatus = Status.Complete;
			})
			.catch(() => {
				this.providerStatus = Status.Error;
			});
	}

	private async loadPreferencesInternal(options?: {
		skipCache?: boolean;
	}): Promise<UserPreferences> {
		if (!this.apolloClient) {
			return Promise.reject('No apollo client');
		}

		const { data } = await this.apolloClient.query<EditorPreferencesQueryType>({
			query: EditorPreferencesQuery,
			fetchPolicy: options?.skipCache ? 'no-cache' : 'cache-first',
		});

		this.userPreferences = parseConfluenceEditorSettings(
			data?.userPreferences?.confluenceEditorSettings,
		);
		return this.userPreferences;
	}

	async loadPreferences(): Promise<UserPreferences> {
		try {
			const userPreferences = await this.loadPreferencesInternal({ skipCache: true });
			// can recover from the status previous error
			if (this.providerStatus !== Status.Complete) {
				this.providerStatus = Status.Complete;
			}
			return userPreferences;
		} catch (error) {
			// We will ignore subssequent errors if thte status was complete
			// And will use the data from previous request
			if (this.providerStatus !== Status.Complete) {
				this.providerStatus = Status.Error;
			}
			return Promise.reject(error);
		}
	}

	async updatePreference<K extends keyof UserPreferences>(
		key: K,
		value: UserPreferences[K],
	): Promise<void> {
		if (!this.apolloClient) {
			return Promise.reject('No apollo client');
		}

		const variables = {
			[key as keyof EditorPreferencesMutationVariables]: value,
		} as EditorPreferencesMutationVariables;

		this.userPreferences[key] = value;

		const { data } = await this.apolloClient.mutate<
			EditorPreferencesMutationResponse,
			EditorPreferencesMutationVariables
		>({
			mutation: EditorPreferencesMutation,
			variables,
		});

		const updatedUserPreferences = parseConfluenceEditorSettings(
			data?.updateUserPreferences?.confluenceEditorSettings,
		);

		this.userPreferences = {
			...this.userPreferences,
			...updatedUserPreferences,
		};
	}

	getPreference<K extends keyof UserPreferences>(key: K): UserPreferences[K] | undefined | null {
		if (this.providerStatus !== Status.Complete) {
			return undefined;
		}
		return this.userPreferences[key] || null;
	}
}
